import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

export type ThemeMode = 'light' | 'dark';

export interface UiState {
  themeMode: ThemeMode;
  sidebarCollapsed: boolean;
}

const STORAGE_KEY_THEME = 'themeMode';
const STORAGE_KEY_SIDEBAR = 'sidebarCollapsed';

function loadInitialMode(): ThemeMode {
  if (typeof window === 'undefined') return 'dark';
  const saved = window.localStorage.getItem(STORAGE_KEY_THEME);
  if (saved === 'light' || saved === 'dark') return saved;
  return 'dark'; // default
}

function loadInitialSidebar(): boolean {
  if (typeof window === 'undefined') return false;
  const saved = window.localStorage.getItem(STORAGE_KEY_SIDEBAR);
  return saved === 'true';
}

const initialState: UiState = {
  themeMode: loadInitialMode(),
  sidebarCollapsed: loadInitialSidebar(),
};

function safeSet(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // ignore (SSR / disabled storage)
  }
}

export const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setThemeMode: (state, action: PayloadAction<ThemeMode>) => {
      state.themeMode = action.payload;
      safeSet(STORAGE_KEY_THEME, action.payload);
    },
    toggleTheme: (state) => {
      state.themeMode = state.themeMode === 'dark' ? 'light' : 'dark';
      safeSet(STORAGE_KEY_THEME, state.themeMode);
    },
    setSidebarCollapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebarCollapsed = action.payload;
      safeSet(STORAGE_KEY_SIDEBAR, String(action.payload));
    },
    toggleSidebar: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
      safeSet(STORAGE_KEY_SIDEBAR, String(state.sidebarCollapsed));
    },
  },
});

export const { setThemeMode, toggleTheme, setSidebarCollapsed, toggleSidebar } = uiSlice.actions;
export default uiSlice.reducer;
